import {
    FlashlightOn,
    KeyboardArrowDown,
    KeyboardArrowUp,
    KeyboardDoubleArrowDown,
    KeyboardDoubleArrowUp,
    Settings,
    SettingsInputComponent,
} from "@mui/icons-material"
import { Box, Grid, IconButton, Tooltip, Typography, useTheme } from "@mui/material"
import { Stack } from "@mui/system"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Operation } from "../../api/Authz"
import { ConfigProto, createConfig, Schedule } from "../../api/AutoArmConfig"
import { Site, Unit } from "../../api/Customer"
import { useAlarmPermission, useUnitPermission } from "../../auth/AuthorizerProvider"
import { updatedSet } from "../../config/set"
import { itemKey } from "../../services/AlarmEvents"
import { useIoT, useIoTCommands, useIoTValues } from "../../services/IoT"
import { ConnectionStatus } from "../common/ConnectionStatus"
import { AutoArmConfigButton } from "./autoarm/AutoArmConfigButton"
import { LightInfo } from "./LightInfo"
import { OutputInfo } from "./OutputInfo"
import { PartitionBlock } from "./PartitionBlock"

const regexAlarmThingName = /[a-zA-Z0-9]+\.alarm_[0-9]+\.(zone|partition|output)_([0-9]+)/
const regexLightThingName = /[a-zA-Z0-9]+\.light_([0-9]+)/

export interface Partition {
    id: number
    displayName?: string
    thingName: string
    alarmID: number
    isSystem: boolean
    stale: boolean
}

export interface Zone {
    id: number
    displayName?: string
    thingName: string
    partitionID: number
    alarmID: number
    isSystem: boolean
    stale: boolean
}

export interface Output {
    id: number
    displayName?: string
    thingName: string
    alarmID: number
    type?: string
    stale: boolean
}

export interface Light {
    id: number
    displayName?: string
    thingName: string
    stale: boolean
}

export interface AlarmProps {
    site: Site
    unit: Unit
}

const classes = ["alarm", "alarm_partition", "alarm_zone", "alarm_output", "auto_arm", "light"]

export function NewAlarm(props: AlarmProps) {
    const { site, unit } = props

    const { online, clients, items, values } = useIoT(site, unit.ShortName, true, classes)
    const alarmPartitions = useIoTValues("alarm_partition", "alarm", clients, items, values)
    const alarmZones = useIoTValues("alarm_zone", "alarm", clients, items, values)
    const outputSwitches = useIoTValues("alarm_output", "switch", clients, items, values)
    const lightSwitches = useIoTValues("light", "switch", clients, items, values)
    const autoArmConfigs = useIoTValues("auto_arm", "config", clients, items, values)

    const autoArmConfig = useMemo(() => {
        const proto = autoArmConfigs[0]?.value?.proto
        if (!proto || proto["@type"] !== ConfigProto.Type) {
            return undefined
        }
        return proto
    }, [autoArmConfigs])

    const { sendNumber, sendProto } = useIoTCommands(site)

    const [states, setStates] = useState<Map<string, boolean>>(new Map())

    const [showOutputs, setShowOutputs] = useState(false)
    const [showLights, setShowLights] = useState(true)
    const [showConfiguration, setShowConfiguration] = useState(true)
    const [showDetails, setShowDetails] = useState(new Set())

    const { t } = useTranslation()
    const theme = useTheme()

    const allowLightOperation = useUnitPermission(Operation.SWITCH_LIGHT, unit)
    const allowUpdateAutoArmConfig = useAlarmPermission(Operation.UPDATE_AUTO_ARM, unit, 1)

    useEffect(() => {
        setStates((prev) => {
            const updatedMap = new Map(prev)
            values.forEach((v) => {
                updatedMap.set(itemKey(v.thing, v.item), v.value.number === 1)
            })
            return updatedMap
        })
    }, [values])

    const flipDetails = (partitionID: number) => setShowDetails((v) => updatedSet(v, partitionID, !v.has(partitionID)))

    const partitions = useMemo<Partition[]>(() => {
        const result: Partition[] = []
        for (const i of alarmPartitions) {
            const matches = i.thing.match(regexAlarmThingName)
            const alarmID = i.labels?.get("alarm")
            if (!matches || !alarmID) {
                continue
            }

            result.push({
                id: +matches[2],
                displayName: i.annotations?.get("display_name"),
                thingName: i.thing,
                isSystem: i.labels?.get("member_system") === "true",
                alarmID: +alarmID,
                stale: i.stale,
            })
        }
        return result
    }, [alarmPartitions])

    const zones = useMemo<Zone[]>(() => {
        const result: Zone[] = []
        for (const i of alarmZones) {
            const matches = i.thing.match(regexAlarmThingName)
            const alarmID = i.labels?.get("alarm")
            if (!matches || !alarmID) {
                continue
            }

            const partitionID = i.labels?.get("partition")
            if (!partitionID) {
                break
            }
            result.push({
                id: +matches[2],
                displayName: i.annotations?.get("display_name"),
                thingName: i.thing,
                isSystem: i.labels?.get("member_system") === "true",
                partitionID: +partitionID,
                alarmID: +alarmID,
                stale: i.stale,
            })
        }
        return result
    }, [alarmZones])

    const outputs = useMemo<Output[]>(() => {
        const result: Output[] = []
        for (const i of outputSwitches) {
            const matches = i.thing.match(regexAlarmThingName)
            const alarmID = i.labels?.get("alarm")
            if (!matches || !alarmID) {
                continue
            }

            result.push({
                id: +matches[2],
                displayName: i.annotations?.get("display_name"),
                thingName: i.thing,
                alarmID: +alarmID,
                type: i.annotations?.get("output_type"),
                stale: i.stale,
            })
        }
        return result
    }, [outputSwitches])

    const lights = useMemo<Light[]>(() => {
        const result: Light[] = []
        for (const i of lightSwitches) {
            const matches = i.thing.match(regexLightThingName)
            if (!matches) {
                continue
            }

            result.push({
                id: +matches[1],
                displayName: i.annotations?.get("display_name"),
                thingName: i.thing,
                stale: i.stale,
            })
        }
        return result
    }, [lightSwitches])

    const partitionState = useCallback(
        (partition: Partition, item: string) => !!states.get(itemKey(partition.thingName, item)),
        [states]
    )
    const zoneState = useCallback((zone: Zone, item: string) => states.get(itemKey(zone.thingName, item)), [states])
    const outputState = useCallback((output: Output) => states.get(itemKey(output.thingName, "switch")), [states])
    const lightState = useCallback((light: Light) => states.get(itemKey(light.thingName, "switch")), [states])

    const showAll = showDetails.size === partitions.length && showOutputs && showLights && showConfiguration

    return (
        <Stack sx={{ py: 2, px: 4, alignItems: "center", width: "100%" }}>
            <Box sx={{ width: "min(100%,1280px)" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <ConnectionStatus online={online} />
                </Stack>
                <Grid container pb={5} spacing={1}>
                    <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Stack direction="row" alignItems="center" pb={2}>
                                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                                    {t("offering.alarm")}
                                </Typography>
                                <Box flexGrow={1} />
                                <Tooltip
                                    title={t(showAll ? "alarm.hide_all_tooltip" : "alarm.show_all_tooltip")}
                                    disableInteractive
                                >
                                    <IconButton
                                        color="primary"
                                        onClick={() => {
                                            if (showAll) {
                                                setShowOutputs(false)
                                                setShowLights(false)
                                                setShowConfiguration(false)
                                                setShowDetails(new Set())
                                            } else {
                                                setShowOutputs(true)
                                                setShowLights(true)
                                                setShowConfiguration(true)
                                                setShowDetails(new Set(partitions.map((p) => p.id)))
                                            }
                                        }}
                                    >
                                        {showAll ? <KeyboardDoubleArrowUp /> : <KeyboardDoubleArrowDown />}
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6} md={8} lg={9} />
                        {partitions.map((partition) => (
                            <Grid
                                key={partition.id}
                                item
                                xs={12}
                                sm={showDetails.has(partition.id) ? 12 : 6}
                                md={showDetails.has(partition.id) ? 12 : 4}
                                lg={showDetails.has(partition.id) ? 12 : 3}
                            >
                                <PartitionBlock
                                    partition={partition}
                                    stale={partition.stale}
                                    zones={zones}
                                    unit={unit}
                                    armed={partitionState(partition, "armed")}
                                    alarm={partitionState(partition, "alarm")}
                                    alarmInMemory={partitionState(partition, "alarm_in_memory")}
                                    showDetails={showDetails.has(partition.id)}
                                    zoneState={zoneState}
                                    flipArmed={(on) => sendNumber(partition.thingName, "armed", on ? 0 : 1)}
                                    flipBypass={(on, zone) => sendNumber(zone.thingName, "bypass", on ? 0 : 1)}
                                    flipDetails={() => flipDetails(partition.id)}
                                />
                            </Grid>
                        ))}

                        {outputs.length > 0 && (
                            <Grid item xs={12}>
                                <Grid container spacing={1} pb={5}>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <SettingsInputComponent htmlColor={theme.palette.text.secondary} />
                                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                                {t("alarm.outputs")}
                                            </Typography>
                                            <Box flexGrow={1} />
                                            <Tooltip
                                                title={t(
                                                    showOutputs
                                                        ? "alarm.hide_outputs_tooltip"
                                                        : "alarm.show_outputs_tooltip"
                                                )}
                                                disableInteractive
                                            >
                                                <IconButton color="primary" onClick={() => setShowOutputs((s) => !s)}>
                                                    {showOutputs ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={8} lg={9} />
                                    {showOutputs &&
                                        outputs.map((output) => (
                                            <OutputInfo
                                                key={output.id}
                                                id={output.id}
                                                displayName={output.displayName}
                                                type={output.type}
                                                unit={unit}
                                                alarmID={output.alarmID}
                                                value={outputState(output)}
                                                stale={output.stale}
                                                flipSwitch={(on) => sendNumber(output.thingName, "switch", on ? 0 : 1)}
                                            />
                                        ))}
                                </Grid>
                            </Grid>
                        )}

                        {!!autoArmConfig && (
                            <Grid item xs={12}>
                                <Grid container spacing={1} pb={5}>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Settings htmlColor={theme.palette.text.secondary} />
                                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                                {t("alarm.configuration")}
                                            </Typography>
                                            <Box flexGrow={1} />
                                            <Tooltip
                                                title={t(
                                                    showConfiguration
                                                        ? "alarm.hide_configuration_tooltip"
                                                        : "alarm.show_configuration_tooltip"
                                                )}
                                                disableInteractive
                                            >
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => setShowConfiguration((s) => !s)}
                                                >
                                                    {showConfiguration ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={8} lg={9} />
                                    {showConfiguration && (
                                        <AutoArmConfigButton
                                            readOnly={!allowUpdateAutoArmConfig}
                                            config={autoArmConfig}
                                            saveConfig={(config?: Schedule, enabled?: boolean) =>
                                                sendProto(
                                                    autoArmConfigs[0].thing,
                                                    "config",
                                                    createConfig(config, enabled)
                                                )
                                            }
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        )}

                        {lights.length > 0 && (
                            <Grid item xs={12}>
                                <Grid container spacing={1} pb={5}>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <FlashlightOn htmlColor={theme.palette.text.secondary} />
                                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                                {t("unit.lights")}
                                            </Typography>
                                            <Box flexGrow={1} />
                                            <Tooltip
                                                title={t(
                                                    showLights ? "unit.hide_lights_tooltip" : "unit.show_lights_tooltip"
                                                )}
                                                disableInteractive
                                            >
                                                <IconButton color="primary" onClick={() => setShowLights((s) => !s)}>
                                                    {showLights ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={8} lg={9} />
                                    {showLights &&
                                        lights.map((light) => (
                                            <LightInfo
                                                key={light.id}
                                                id={light.id}
                                                displayName={light.displayName}
                                                value={lightState(light)}
                                                allowed={allowLightOperation && !light.stale}
                                                flipSwitch={(on) => sendNumber(light.thingName, "switch", on ? 0 : 1)}
                                            />
                                        ))}
                                </Grid>
                            </Grid>
                        )}
                    </>
                </Grid>
            </Box>
        </Stack>
    )
}
